<template>
    <div class="truncated" :class="{ 'truncated-visible': expanded }">
        <TransitionExpand>
            <div
                ref="wrapper"
                class="truncated-wrapper"
                :class="{ transition: initiated }"
                :role="wrapperClickable ? 'button' : ''"
                :onClick="() => (wrapperClickable ? toggleExpanded() : null)"
                :style="{
                    height: expanded ? `${fullHeight}px` : `${collapsedHeight}px`,
                }">
                <div
                    ref="element"
                    class="truncated-text"
                    v-html="cleanText"
                    :style="isTruncated && { '--lines': rows }"
                    :class="{
                        isTruncated: !expanded && isTruncated,
                    }" />
            </div>
        </TransitionExpand>

        <ButtonLink v-if="showButton" :onClick="toggleExpanded" size="m" :noPadding="true">
            {{ expanded ? t({ key: 'shared.show_less' }) : t({ key: 'shared.show_more' }) }}
        </ButtonLink>
    </div>
</template>

<script lang="ts">
    import { t } from '@shared/helpers/translation';
    import {
        computed,
        defineComponent,
        nextTick,
        onMounted,
        onUnmounted,
        ref,
        watch,
    } from 'vue';
    import { useElementVisibility } from '@vueuse/core';
    import { scrollToElement } from '@shared/helpers/scroll';
    import sanitize from 'sanitize-html';
    import TransitionExpand from '../TransitionExpand/TransitionExpand.vue';
    import ButtonLink from '../Button/ButtonLink.vue';

    export default defineComponent({
        name: 'TruncateText',
        methods: { t },
        components: { ButtonLink, TransitionExpand },
        props: {
            text: { type: String, required: true },
            once: { type: Boolean, default: false },
            rows: { type: Number, default: 1 },
            onExpand: { type: Function },
            hideButton: { type: Boolean, default: false },
        },
        setup(props) {
            const element = ref<HTMLElement | null>(null);
            const wrapper = ref<HTMLElement | null>(null);

            const isTruncated = ref(false);
            const expanded = ref<boolean>(false);
            const fullHeight = ref(0);
            const collapsedHeight = ref(0);
            const initiated = ref(false);

            watch(wrapper, (newValue) => {
                if (newValue && !initiated.value) {
                    nextTick(() => {
                        const height = parseFloat(getComputedStyle(newValue).height);
                        initiated.value = height > 0;
                    });
                }
            });

            const cleanText = computed(() => sanitize(props.text));

            const showButton = computed(
                () =>
                    !props.hideButton &&
                    isTruncated.value &&
                    ((expanded.value && !props.once) || !expanded.value)
            );

            const calculateHeights = () => {
                if (element.value) {
                    const lineHeight = parseFloat(getComputedStyle(element.value).lineHeight);

                    const maxHeight = lineHeight * props.rows;
                    const { scrollHeight } = element.value;

                    const height = scrollHeight <= maxHeight ? scrollHeight : maxHeight;
                    collapsedHeight.value = height;

                    element.value.style.height = 'auto';
                    fullHeight.value = scrollHeight;

                    isTruncated.value = fullHeight.value > height;

                    if (!expanded.value) {
                        element.value.style.height = `${height}px`;
                    }
                }
            };

            const wrapperClickable = computed(() => props.hideButton && isTruncated.value);

            const targetIsFullyVisible = useElementVisibility(wrapper, {
                threshold: 1,
            });

            const toggleExpanded = () => {
                if (expanded.value && !targetIsFullyVisible.value) {
                    setTimeout(() => scrollToElement(wrapper.value, -50, false), 40);
                }

                expanded.value = !expanded.value;

                if (expanded.value) {
                    props.onExpand && props.onExpand();
                }
            };

            onMounted(() => {
                calculateHeights();

                if (wrapper.value) {
                    const observer = new ResizeObserver(() => {
                        calculateHeights();
                    });

                    observer.observe(wrapper.value);
                }
            });

            onUnmounted(() => {
                if (wrapper.value) {
                    wrapper.value.removeEventListener('resize', calculateHeights);
                }
            });

            watch(
                () => [cleanText.value, props.rows],
                () => calculateHeights()
            );

            return {
                cleanText,
                element,
                wrapper,
                wrapperClickable,
                showButton,
                expanded,
                isTruncated,
                toggleExpanded,
                fullHeight,
                collapsedHeight,
                initiated,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @forward './TruncateText.scss';
</style>
